import { cn } from '@repo/ui/lib/utils'
import type { ReactNode } from 'react'

export function MarkdownPreview({
  markdown,
  className,
}: {
  markdown: string
  className?: string
}) {
  const blocks = markdown.trim().split(/\n{2,}/).filter(Boolean)

  if (blocks.length === 0) {
    return <div className={cn('text-sm text-muted-foreground', className)} />
  }

  return (
    <div className={cn('space-y-4 text-sm leading-6 text-foreground', className)}>
      {blocks.map((block, index) => (
        <MarkdownBlock key={`${block}-${index}`} block={block} />
      ))}
    </div>
  )
}

function MarkdownBlock({ block }: { block: string }) {
  const lines = block.split('\n')
  const firstLine = lines[0]?.trim() ?? ''

  if (firstLine.startsWith('### ')) {
    return <h4 className="text-base font-semibold">{renderInline(firstLine.slice(4))}</h4>
  }

  if (firstLine.startsWith('## ')) {
    return <h3 className="text-lg font-semibold">{renderInline(firstLine.slice(3))}</h3>
  }

  if (firstLine.startsWith('# ')) {
    return <h2 className="text-xl font-semibold">{renderInline(firstLine.slice(2))}</h2>
  }

  if (lines.every((line) => /^[-*]\s+/.test(line.trim()))) {
    return (
      <ul className="space-y-1 pl-5">
        {lines.map((line) => (
          <li key={line} className="list-disc">
            {renderInline(line.trim().replace(/^[-*]\s+/, ''))}
          </li>
        ))}
      </ul>
    )
  }

  if (lines.every((line) => /^\d+\.\s+/.test(line.trim()))) {
    return (
      <ol className="space-y-1 pl-5">
        {lines.map((line) => (
          <li key={line} className="list-decimal">
            {renderInline(line.trim().replace(/^\d+\.\s+/, ''))}
          </li>
        ))}
      </ol>
    )
  }

  if (lines.every((line) => line.trim().startsWith('> '))) {
    return (
      <blockquote className="border-l-2 border-border pl-3 text-muted-foreground">
        {lines.map((line) => (
          <p key={line}>{renderInline(line.trim().slice(2))}</p>
        ))}
      </blockquote>
    )
  }

  return (
    <p className="whitespace-pre-line text-muted-foreground">
      {lines.map((line, index) => (
        <span key={`${line}-${index}`}>
          {index > 0 ? '\n' : null}
          {renderInline(line)}
        </span>
      ))}
    </p>
  )
}

function renderInline(value: string): ReactNode {
  const parts = value.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).filter(Boolean)

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={`${part}-${index}`}
          className="rounded-[4px] border border-border/70 bg-muted/30 px-1 py-0.5 text-[0.92em]"
        >
          {part.slice(1, -1)}
        </code>
      )
    }

    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={`${part}-${index}`}>{part.slice(1, -1)}</em>
    }

    return part
  })
}
